<template>
  <BaseModal
    :modalTitle="$t('W: Send igen')"
    :modalIntro="$t('W: Sender alle rapporter og bilag igen for denne levering')">
    <p class="mb-4 text-sm text-text-secondary">
      {{ $t('W: Adskil e-mailadresser med komma') }}
    </p>
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :onSubmit="onSubmit">
      <UiInputTextAreaVee
        name="emails"
        :label="$t('form.label.email-addresses')"
        :placeholder="$t('form.placeholder.email-addresses')"
        v-model="emailsText"
        @update:model-value="resetValidationMessages" />

      <span class="mb-2 block text-sm text-text-primary">
        {{ $t('W: Antal emails') }}: {{ emailTokens.length }}
      </span>

      <InfoText
        v-if="validationText"
        :text="validationText"
        class="mt-2 mb-4"
        theme="warning" />

      <div class="mb-4 flex items-center gap-2">
        <AppSwitch
          :isActive="recreateAllReports"
          :title="recreateAllReports ? $t('action.deactivate') : $t('action.activate')"
          @on-toggle="recreateAllReports = !recreateAllReports" />
        <span class="text-sm text-text-primary">{{ $t('W: Opret alle rapporter på ny') }}</span>
      </div>

      <template #buttons>
        <UiBaseButton
          :text="$t('action.cancel')"
          theme="neutral"
          @click="$emit('close')" />
        <UiBaseButton
          :text="$t('W: Send igen')"
          type="submit"
          :isDisabled="!isFormValid"
          :isLoading="isLoading"
          :showSaveIcon="showSaveIcon" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import { mapActions, mapState } from 'pinia'
import { useAppStore } from '@/stores/appStore'
import { useOrderStore } from '@/stores/orderStore'
import { useModalStore } from '@/stores/modalStore'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'
import { useToast } from '@/globals/typescript/utils/toast'
import { union } from 'lodash-es'
import isEmail from 'validator/lib/isEmail'

const MAX_RECIPIENT_EMAILS = 5

export default {
  name: 'OrderResendReportsModal',

  emits: ['close'],

  props: {
    orderID: { type: String, required: true },
    tatGroupID: { type: String, required: true },
    currentEmails: { type: Array as () => string[], required: true },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      emailsText: this.currentEmails.join(', '),
      recreateAllReports: false,
      errorText: '',
      isLoading: false,
      showSaveIcon: false,
    }
  },

  computed: {
    ...mapState(useAppStore, ['defaultMessageSaveIconTime']),
    emailTokens(): string[] {
      return union(
        this.emailsText
          .split(',')
          .map((email) => email.trim())
          .filter((email) => email.length > 0)
      )
    },
    invalidEmails(): string[] {
      return this.emailTokens.filter(
        (email) => !isEmail(email, { allow_utf8_local_part: true })
      )
    },
    validationText(): string {
      if (this.invalidEmails.length > 0) {
        const label =
          this.invalidEmails.length === 1
            ? this.$t('W: Ugyldig email')
            : this.$t('W: Ugyldige emails')
        return `${label}: ${this.invalidEmails.join(', ')}`
      }
      if (this.emailTokens.length > MAX_RECIPIENT_EMAILS) {
        return this.$t('W: Maks. {count} modtagere', {
          count: MAX_RECIPIENT_EMAILS,
        })
      }
      return ''
    },
    isFormValid(): boolean {
      return (
        this.emailTokens.length > 0 &&
        this.emailTokens.length <= MAX_RECIPIENT_EMAILS &&
        this.invalidEmails.length === 0
      )
    },
  },

  methods: {
    ...mapActions(useOrderStore, ['resendOrderReports']),

    async onSubmit(): Promise<void> {
      if (!this.isFormValid) return

      this.isLoading = true
      this.errorText = ''
      try {
        await this.resendOrderReports(
          this.orderID,
          this.tatGroupID,
          this.emailTokens,
          this.recreateAllReports
        )
        this.showSaveIcon = true
        setTimeout(() => {
          this.showSaveIcon = false
        }, this.defaultMessageSaveIconTime)
        this.unsavedGuard.takeSnapshot()
        useToast().success(this.$t('W: Rapporter sendt igen'))
        this.$emit('close')
      } catch {
        this.errorText = this.$t('error.common.standard')
        useToast().error(this.$t('W: Kunne ikke sende rapporter igen'))
      } finally {
        this.isLoading = false
      }
    },

    resetValidationMessages(): void {
      this.errorText = ''
    },
  },

  created() {
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({
        emailsText: this.emailsText,
        recreateAllReports: this.recreateAllReports,
      }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
